import React, { useState, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { 
  Upload, 
  Image as ImageIcon, 
  X, 
  Link as LinkIcon,
  Camera,
  Globe,
  Trash2
} from 'lucide-react'
import { toast } from 'sonner'
import { useImageUpload } from '@/services/imageUploadService'

interface AdvancedImageUploadProps {
  onImageUploaded: (url: string) => void
  onImageUrlChange: (url: string) => void
  currentUrl?: string
  multiple?: boolean
  accept?: string
  maxSize?: number // en MB
}

export const AdvancedImageUpload: React.FC<AdvancedImageUploadProps> = ({
  onImageUploaded,
  onImageUrlChange,
  currentUrl = '',
  multiple = false,
  accept = 'image/*',
  maxSize = 10 // Augmenté à 10MB
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadImage, createPreviewUrl, revokePreviewUrl } = useImageUpload()

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files)
    }
  }, [])

  const handleFiles = async (files: FileList) => {
    const file = files[0]
    
    if (!file) return

    setUploading(true)
    
    try {
      // Upload réel vers Supabase Storage
      const result = await uploadImage(file, 'covers')
      onImageUploaded(result.url)
      
      toast.success('Image uploadée avec succès!')
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.message || 'Erreur lors de l\'upload de l\'image')
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files)
    }
  }

  const handleUrlSubmit = () => {
    if (imageUrl.trim()) {
      // Vérifier que l'URL est valide
      try {
        new URL(imageUrl)
        onImageUrlChange(imageUrl)
        setImageUrl('')
        setShowUrlInput(false)
        toast.success('URL de l\'image ajoutée!')
      } catch {
        toast.error('URL invalide')
      }
    }
  }

  const removeImage = () => {
    onImageUrlChange('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  const generateUnsplashUrl = () => {
    const width = 800
    const height = 400
    const unsplashUrl = `https://source.unsplash.com/${width}x${height}/?business,office,work`
    onImageUrlChange(unsplashUrl)
    toast.success('Image Unsplash ajoutée!')
  }

  return (
    <div className="space-y-4">
      {/* Zone d'upload */}
      {!currentUrl && (
        <Card>
          <CardContent className="p-6">
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
                }
                ${uploading ? 'opacity-50 pointer-events-none' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Upload className="w-12 h-12 text-gray-400" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-700">
                    {uploading ? 'Upload en cours...' : 'Ajoutez une image'}
                  </h3>
                  <p className="text-gray-500 mt-2">
                    Glissez-déposez une image ici ou cliquez pour sélectionner
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PNG, JPG, GIF jusqu'à {maxSize}MB
                  </p>
                </div>
                
                <div className="flex flex-wrap justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openFileDialog}
                    disabled={uploading}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Choisir un fichier
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUrlInput(!showUrlInput)}
                    disabled={uploading}
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    URL
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateUnsplashUrl}
                    disabled={uploading}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Unsplash
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Input URL */}
      {showUrlInput && !currentUrl && (
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://exemple.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleUrlSubmit}
                disabled={!imageUrl.trim()}
              >
                Ajouter
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowUrlInput(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prévisualisation */}
      {currentUrl && (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Image sélectionnée
                </h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeImage}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="relative">
                <img
                  src={currentUrl}
                  alt="Prévisualisation"
                  className="w-full max-h-64 object-cover rounded-lg border"
                  onError={() => {
                    toast.error('Erreur lors du chargement de l\'image')
                  }}
                />
              </div>
              
              <div className="text-xs text-gray-500">
                URL: {currentUrl}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
} 